import type {
  KeystrokeEvent,
  TypingSessionSummary,
} from "../measurement";

export type ProfessionalKeyState = "steady" | "watch" | "needs-practice";

export interface ProfessionalTimeSample {
  startMs: number;
  endMs: number;
  attempts: number;
  accurateWpm: number | null;
  accuracy: number | null;
}

export interface ProfessionalKeyStat {
  key: string;
  label: string;
  state: ProfessionalKeyState;
  indicator: "✓" | "~" | "!";
  attempts: number;
  errors: number;
  errorRate: number;
  averageIntervalMs: number | null;
}

export interface ProfessionalPatternStat {
  pattern: string;
  label: string;
  size: 2 | 3;
  attempts: number;
  errors: number;
  errorRate: number;
  averageIntervalMs: number | null;
}

export interface ProfessionalWordStat {
  word: string;
  position: number;
  attempts: number;
  errors: number;
  averageIntervalMs: number | null;
}

export interface ProfessionalHesitation {
  sourceIndex: number;
  expectedLabel: string;
  gapMs: number;
}

export interface ProfessionalRhythmSummary {
  medianIntervalMs: number | null;
  p90IntervalMs: number | null;
  hesitationThresholdMs: number | null;
  hesitationCount: number;
  label: "insufficient-data" | "steady" | "variable" | "uneven";
}

export interface ProfessionalCorrectionSummary {
  initialErrors: number;
  correctedErrors: number;
  unresolvedErrors: number;
  correctionRate: number | null;
}

export interface ProfessionalSessionAnalytics {
  schemaVersion: 1;
  pace: ProfessionalTimeSample[];
  keys: ProfessionalKeyStat[];
  patterns: ProfessionalPatternStat[];
  slowWords: ProfessionalWordStat[];
  hesitations: ProfessionalHesitation[];
  rhythm: ProfessionalRhythmSummary;
  corrections: ProfessionalCorrectionSummary;
  summary: string;
}

interface EventWithInterval {
  event: KeystrokeEvent;
  intervalMs: number | null;
}

const MAX_ANALYSIS_INTERVAL_MS = 5_000;
const PACE_BUCKET_MS = 5_000;

function round(value: number, digits = 0): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function percentile(values: number[], fraction: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.ceil(sorted.length * fraction) - 1),
  );
  return sorted[index] ?? null;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2;
  }
  return sorted[middle] ?? null;
}

export function formatProfessionalAnalyticsToken(value: string): string {
  if (value === " ") return "Space";
  if (value === "\n") return "Enter";
  if (value === "\t") return "Tab";
  return value;
}

function visiblePattern(value: string): string {
  return [...value]
    .map((character) => {
      if (character === " ") return "␠";
      if (character === "\n") return "↵";
      if (character === "\t") return "⇥";
      return character;
    })
    .join("");
}

function scoredEvents(events: readonly KeystrokeEvent[]): KeystrokeEvent[] {
  return events
    .filter(
      (event) =>
        event.expected !== null &&
        event.actual !== null &&
        event.isCorrectAtEntry !== null,
    )
    .sort((a, b) => a.sequence - b.sequence);
}

function withIntervals(
  events: readonly KeystrokeEvent[],
): EventWithInterval[] {
  let previousElapsed: number | null = null;
  return events.map((event) => {
    const rawInterval =
      previousElapsed === null
        ? null
        : event.activeElapsedMs - previousElapsed;
    previousElapsed = event.activeElapsedMs;
    return {
      event,
      intervalMs:
        rawInterval !== null &&
        rawInterval >= 0 &&
        rawInterval <= MAX_ANALYSIS_INTERVAL_MS
          ? rawInterval
          : null,
    };
  });
}

function buildPaceSeries(
  events: readonly KeystrokeEvent[],
  activeDurationMs: number,
): ProfessionalTimeSample[] {
  if (events.length === 0 || activeDurationMs <= 0) return [];
  const bucketCount = Math.max(1, Math.ceil(activeDurationMs / PACE_BUCKET_MS));
  const samples: ProfessionalTimeSample[] = [];
  for (let bucket = 0; bucket < bucketCount; bucket += 1) {
    const startMs = bucket * PACE_BUCKET_MS;
    const endMs = Math.min(activeDurationMs, startMs + PACE_BUCKET_MS);
    const bucketEvents = events.filter(
      (event) =>
        event.activeElapsedMs > startMs &&
        event.activeElapsedMs <= endMs,
    );
    const attempts = bucketEvents.length;
    const correct = bucketEvents.filter(
      (event) => event.isCorrectAtEntry,
    ).length;
    const durationMs = Math.max(1_000, endMs - startMs);
    samples.push({
      startMs,
      endMs,
      attempts,
      accurateWpm:
        attempts === 0
          ? null
          : round(correct / 5 / (durationMs / 60_000), 1),
      accuracy:
        attempts === 0 ? null : round((correct / attempts) * 100, 1),
    });
  }
  return samples;
}

function keyState(
  errors: number,
  attempts: number,
  averageIntervalMs: number | null,
  baselineIntervalMs: number | null,
): Pick<ProfessionalKeyStat, "state" | "indicator"> {
  const errorRate = attempts > 0 ? errors / attempts : 0;
  if (errors >= 2 || errorRate >= 0.2) {
    return { state: "needs-practice", indicator: "!" };
  }
  if (
    errors > 0 ||
    (averageIntervalMs !== null &&
      baselineIntervalMs !== null &&
      averageIntervalMs > baselineIntervalMs * 1.35)
  ) {
    return { state: "watch", indicator: "~" };
  }
  return { state: "steady", indicator: "✓" };
}

function buildKeyStats(
  entries: readonly EventWithInterval[],
  baselineIntervalMs: number | null,
): ProfessionalKeyStat[] {
  const grouped = new Map<
    string,
    { attempts: number; errors: number; intervals: number[] }
  >();
  entries.forEach(({ event, intervalMs }) => {
    const key = event.expected ?? "";
    const current = grouped.get(key) ?? {
      attempts: 0,
      errors: 0,
      intervals: [],
    };
    current.attempts += 1;
    if (!event.isCorrectAtEntry) current.errors += 1;
    if (intervalMs !== null) current.intervals.push(intervalMs);
    grouped.set(key, current);
  });

  return [...grouped.entries()]
    .map(([key, value]) => {
      const averageIntervalMs =
        value.intervals.length > 0
          ? round(
              value.intervals.reduce((sum, interval) => sum + interval, 0) /
                value.intervals.length,
            )
          : null;
      return {
        key,
        label: formatProfessionalAnalyticsToken(key),
        ...keyState(
          value.errors,
          value.attempts,
          averageIntervalMs,
          baselineIntervalMs,
        ),
        attempts: value.attempts,
        errors: value.errors,
        errorRate: round((value.errors / value.attempts) * 100, 1),
        averageIntervalMs,
      };
    })
    .sort(
      (a, b) =>
        b.errors - a.errors ||
        b.errorRate - a.errorRate ||
        (b.averageIntervalMs ?? 0) - (a.averageIntervalMs ?? 0) ||
        a.label.localeCompare(b.label),
    );
}

function originalAttemptEvents(
  events: readonly KeystrokeEvent[],
): KeystrokeEvent[] {
  const firstByIndex = new Map<number, KeystrokeEvent>();
  events.forEach((event) => {
    if (!firstByIndex.has(event.sourceIndex)) {
      firstByIndex.set(event.sourceIndex, event);
    }
  });
  return [...firstByIndex.values()].sort(
    (a, b) => a.sourceIndex - b.sourceIndex,
  );
}

function buildPatternStats(
  events: readonly KeystrokeEvent[],
): ProfessionalPatternStat[] {
  const attempts = originalAttemptEvents(events);
  const grouped = new Map<
    string,
    {
      pattern: string;
      size: 2 | 3;
      attempts: number;
      errors: number;
      intervals: number[];
    }
  >();

  ([2, 3] as const).forEach((size) => {
    for (let index = 0; index <= attempts.length - size; index += 1) {
      const window = attempts.slice(index, index + size);
      const consecutive = window.every(
        (event, offset) =>
          offset === 0 ||
          event.sourceIndex ===
            (window[offset - 1]?.sourceIndex ?? event.sourceIndex) + 1,
      );
      if (!consecutive) continue;
      const pattern = window.map((event) => event.expected ?? "").join("");
      if (pattern.length !== size) continue;
      const key = `${size}:${pattern}`;
      const current = grouped.get(key) ?? {
        pattern,
        size,
        attempts: 0,
        errors: 0,
        intervals: [],
      };
      current.attempts += 1;
      if (window.some((event) => !event.isCorrectAtEntry)) {
        current.errors += 1;
      }
      for (let offset = 1; offset < window.length; offset += 1) {
        const gap =
          (window[offset]?.activeElapsedMs ?? 0) -
          (window[offset - 1]?.activeElapsedMs ?? 0);
        if (gap >= 0 && gap <= MAX_ANALYSIS_INTERVAL_MS) {
          current.intervals.push(gap);
        }
      }
      grouped.set(key, current);
    }
  });

  return [...grouped.values()]
    .map((value) => ({
      pattern: value.pattern,
      label: visiblePattern(value.pattern),
      size: value.size,
      attempts: value.attempts,
      errors: value.errors,
      errorRate: round((value.errors / value.attempts) * 100, 1),
      averageIntervalMs:
        value.intervals.length > 0
          ? round(
              value.intervals.reduce((sum, item) => sum + item, 0) /
                value.intervals.length,
            )
          : null,
    }))
    .sort(
      (a, b) =>
        b.errors - a.errors ||
        b.errorRate - a.errorRate ||
        (b.averageIntervalMs ?? 0) - (a.averageIntervalMs ?? 0) ||
        b.attempts - a.attempts,
    )
    .slice(0, 8);
}

function buildSlowWords(
  sourceText: string,
  events: readonly KeystrokeEvent[],
): ProfessionalWordStat[] {
  const attempts = originalAttemptEvents(events);
  const words = [...sourceText.matchAll(/\S+/gu)];
  return words
    .map((match) => {
      const word = match[0];
      const start = match.index ?? 0;
      const end = start + word.length;
      const wordEvents = attempts.filter(
        (event) => event.sourceIndex >= start && event.sourceIndex < end,
      );
      const intervals = wordEvents
        .slice(1)
        .map(
          (event, index) =>
            event.activeElapsedMs -
            (wordEvents[index]?.activeElapsedMs ?? event.activeElapsedMs),
        )
        .filter(
          (interval) =>
            interval >= 0 && interval <= MAX_ANALYSIS_INTERVAL_MS,
        );
      return {
        word,
        position: start + 1,
        attempts: wordEvents.length,
        errors: wordEvents.filter((event) => !event.isCorrectAtEntry).length,
        averageIntervalMs:
          intervals.length > 0
            ? round(
                intervals.reduce((sum, interval) => sum + interval, 0) /
                  intervals.length,
              )
            : null,
      };
    })
    .filter((word) => word.attempts >= 2)
    .sort(
      (a, b) =>
        b.errors - a.errors ||
        (b.averageIntervalMs ?? 0) - (a.averageIntervalMs ?? 0) ||
        b.attempts - a.attempts,
    )
    .slice(0, 6);
}

function buildRhythm(
  entries: readonly EventWithInterval[],
): {
  rhythm: ProfessionalRhythmSummary;
  hesitations: ProfessionalHesitation[];
} {
  const intervals = entries
    .map((entry) => entry.intervalMs)
    .filter((interval): interval is number => interval !== null);
  const medianIntervalMs = median(intervals);
  const p90IntervalMs = percentile(intervals, 0.9);
  if (medianIntervalMs === null || p90IntervalMs === null) {
    return {
      rhythm: {
        medianIntervalMs: null,
        p90IntervalMs: null,
        hesitationThresholdMs: null,
        hesitationCount: 0,
        label: "insufficient-data",
      },
      hesitations: [],
    };
  }
  const hesitationThresholdMs = Math.max(650, medianIntervalMs * 2.2);
  const hesitations = entries
    .filter(
      (entry) =>
        entry.intervalMs !== null &&
        entry.intervalMs >= hesitationThresholdMs,
    )
    .map((entry) => ({
      sourceIndex: entry.event.sourceIndex,
      expectedLabel: formatProfessionalAnalyticsToken(
        entry.event.expected ?? "",
      ),
      gapMs: round(entry.intervalMs ?? 0),
    }))
    .sort((a, b) => b.gapMs - a.gapMs)
    .slice(0, 6);
  const ratio = p90IntervalMs / Math.max(1, medianIntervalMs);
  return {
    rhythm: {
      medianIntervalMs: round(medianIntervalMs),
      p90IntervalMs: round(p90IntervalMs),
      hesitationThresholdMs: round(hesitationThresholdMs),
      hesitationCount: hesitations.length,
      label: ratio <= 1.7 ? "steady" : ratio <= 2.4 ? "variable" : "uneven",
    },
    hesitations,
  };
}

function buildCorrectionSummary(
  summary: TypingSessionSummary,
): ProfessionalCorrectionSummary {
  return {
    initialErrors: summary.initialErrors,
    correctedErrors: summary.correctedInitialErrors,
    unresolvedErrors: summary.uncorrectedErrors,
    correctionRate:
      summary.initialErrors > 0
        ? round(
            (summary.correctedInitialErrors / summary.initialErrors) * 100,
            1,
          )
        : null,
  };
}

function buildSummary(
  rhythm: ProfessionalRhythmSummary,
  keys: ProfessionalKeyStat[],
  corrections: ProfessionalCorrectionSummary,
): string {
  const flaggedKeys = keys.filter(
    (key) => key.state === "needs-practice",
  ).length;
  const rhythmCopy =
    rhythm.label === "insufficient-data"
      ? "There was not enough interval evidence to classify rhythm."
      : `Rhythm was ${rhythm.label}, with ${rhythm.hesitationCount} notable hesitation${rhythm.hesitationCount === 1 ? "" : "s"}.`;
  const keyCopy =
    flaggedKeys === 0
      ? "No measured key crossed the needs-practice threshold."
      : `${flaggedKeys} measured key${flaggedKeys === 1 ? "" : "s"} crossed the needs-practice threshold.`;
  const correctionCopy =
    corrections.initialErrors === 0
      ? "No initial errors were recorded."
      : `${corrections.correctedErrors} of ${corrections.initialErrors} initial errors were corrected before completion.`;
  return `${rhythmCopy} ${keyCopy} ${correctionCopy}`;
}

export function buildProfessionalSessionAnalytics(
  events: readonly KeystrokeEvent[],
  sourceText: string,
  summary: TypingSessionSummary,
): ProfessionalSessionAnalytics {
  const scored = scoredEvents(events);
  const entries = withIntervals(scored);
  const baselineIntervalMs = median(
    entries
      .map((entry) => entry.intervalMs)
      .filter((interval): interval is number => interval !== null),
  );
  const keys = buildKeyStats(entries, baselineIntervalMs);
  const { rhythm, hesitations } = buildRhythm(entries);
  const corrections = buildCorrectionSummary(summary);
  return {
    schemaVersion: 1,
    pace: buildPaceSeries(scored, summary.activeDurationMs),
    keys,
    patterns: buildPatternStats(scored),
    slowWords: buildSlowWords(sourceText, scored),
    hesitations,
    rhythm,
    corrections,
    summary: buildSummary(rhythm, keys, corrections),
  };
}
